import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LOADING_STATES, ERROR_MESSAGES } from '../utils/constants';

/**
 * Hook for fetching dashboard data from the RDS database
 * @param {string} endpoint - The API endpoint to fetch from
 * @param {object} params - Query parameters for the request
 * @param {object} options - Additional options for the request
 * @returns {object} - Data, loading state, error, and refetch function
 */
export const useDashboardData = (endpoint, params = {}, options = {}) => {
  // Convert params object to query string
  const queryString = new URLSearchParams(params).toString();
  const url = `${process.env.REACT_APP_API_BASE_URL}/${endpoint}${queryString ? `?${queryString}` : ''}`;
  
  // Use React Query for data fetching with caching
  const { 
    data, 
    isLoading, 
    isError, 
    error, 
    refetch, 
    isFetching 
  } = useQuery({
    queryKey: [endpoint, params],
    queryFn: async () => {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer your-token-here', // Replace with actual token if needed
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch data from ${endpoint}`);
      }
      return response.json();
    },
    staleTime: options.staleTime || 5 * 60 * 1000, // 5 minutes by default
    refetchOnWindowFocus: options.refetchOnWindowFocus || false,
    retry: options.retry || 2,
    ...options
  });

  // Determine the status based on loading and error states
  const status = isLoading 
    ? LOADING_STATES.LOADING 
    : isError 
      ? LOADING_STATES.ERROR 
      : data 
        ? LOADING_STATES.SUCCESS 
        : LOADING_STATES.IDLE;
  
  // Format the error message
  const errorMessage = isError 
    ? error?.message || ERROR_MESSAGES.FETCH_FAILED 
    : null;
  
  return {
    data: data || [],
    loading: isLoading || isFetching,
    error: errorMessage,
    status,
    refetch,
    isFetching
  };
};

export default useDashboardData;
